import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Archive, CalendarDays, CheckCircle2, ChevronRight, Clock3, Copy, Download, File as FileIcon,
  FileArchive, FileText, FolderOpen, Image as ImageIcon, MessageSquare, MoreHorizontal,
  Paperclip, RefreshCcw, Send, Upload,
} from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { eur } from "@/components/accounting/docUtils";
import { fmtD, fmtDT, gstatus } from "./gUtils";
import { toast } from "sonner";

const PREVIEW_IMG = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const KIND_LABELS = { travail: "Brouillon interne", apercu: "Envoyé au client", livraison: "Fichier final" };
const KIND_CLS = {
  travail: "bg-neutral-100 text-neutral-500 border-neutral-200",
  apercu: "bg-blue-50 text-blue-700 border-blue-200",
  livraison: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const fmtSize = (b = 0) => (b >= 1048576 ? `${(b / 1048576).toFixed(1).replace(".", ",")} Mo` : b >= 1024 ? `${Math.round(b / 1024)} Ko` : `${b} o`);
const relTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "à l'instant";
  if (h < 24) return `il y a ${h} heure${h > 1 ? "s" : ""}`;
  const d = Math.floor(h / 24);
  return `il y a ${d} jour${d > 1 ? "s" : ""}`;
};
const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const fileUrl = (fid) => `${API}/graphisme/files/${fid}`;

function fileMeta(name = "", ct = "") {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (PREVIEW_IMG.includes(ct)) return { Icon: ImageIcon, bg: "#DBEAFE", fg: "#1D4ED8" };
  if (ext === "pdf" || ct === "application/pdf") return { Icon: FileText, bg: "#FEE2E2", fg: "#DC2626" };
  if (["zip", "rar", "7z"].includes(ext)) return { Icon: FileArchive, bg: "#FEF3C7", fg: "#B45309" };
  if (["ai", "psd", "eps", "svg", "indd"].includes(ext)) return { Icon: FileIcon, bg: "#FFEDD5", fg: "#C2410C" };
  return { Icon: FileIcon, bg: "#F1F5F9", fg: "#64748B" };
}

export default function ProjectPanel({ projectId, clients, meta, onChanged, onEdit }) {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("details");
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentInternal, setCommentInternal] = useState(true);
  const [desc, setDesc] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [versionTarget, setVersionTarget] = useState(null);
  const [versionKind, setVersionKind] = useState("apercu");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    if (!projectId) return;
    api.get(`/graphisme/projects/${projectId}`)
      .then((r) => { setP(r.data); setDesc(r.data.description || r.data.brief || ""); })
      .catch(() => setP(null));
  };
  useEffect(() => { setTab("details"); setComments([]); load(); }, [projectId]); // eslint-disable-line
  useEffect(() => {
    if (tab === "commentaires" && projectId) {
      api.get(`/graphisme/projects/${projectId}/comments`).then((r) => setComments(r.data || [])).catch(() => {});
    }
  }, [tab, projectId]);

  const versions = useMemo(() => (p?.creations || [])
    .flatMap((c) => (c.versions || []).filter((v) => !v.archived).map((v) => ({ ...v, creation: c })))
    .sort((a, b) => (b.at || "").localeCompare(a.at || "")), [p]);
  const allFiles = useMemo(() => versions
    .flatMap((v) => (v.files || []).map((f) => ({ ...f, vlabel: v.label, at: v.at, by: v.by }))), [versions]);
  const thumb = useMemo(() => {
    for (const v of versions) {
      const f = (v.files || []).find((x) => PREVIEW_IMG.includes(x.content_type));
      if (f) return f.id;
    }
    return null;
  }, [versions]);

  if (!projectId) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex items-center justify-center min-h-0" data-testid="gpanel-empty">
        <p className="text-sm text-neutral-400">Sélectionnez un projet pour afficher sa fiche.</p>
      </div>
    );
  }
  if (!p) {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex items-center justify-center min-h-0" data-testid="gpanel-loading">
        <p className="text-sm text-neutral-400">Chargement du projet…</p>
      </div>
    );
  }

  const st = gstatus(p.status);
  const modCount = (p.creations || []).reduce((n, c) => n + (c.mod_requests || []).length, 0);
  const validationState = (() => {
    const cs = p.creations || [];
    if (cs.some((c) => c.status === "modifications_demandees")) return { label: "Modification demandée", sub: "retour client à traiter", cls: "text-[#B45309]", bg: "#FEF3E2", fg: "#B45309" };
    if (cs.some((c) => c.status === "envoyee_validation")) return { label: "En attente", sub: "de validation client", cls: "text-[#B45309]", bg: "#FEF3E2", fg: "#B45309" };
    if (cs.length && cs.every((c) => c.status === "validee")) return { label: "Validé", sub: "par le client", cls: "text-emerald-700", bg: "#DCFCE7", fg: "#15803D" };
    return { label: "En production", sub: "pas encore envoyé", cls: "text-[#1D4ED8]", bg: "#DBEAFE", fg: "#1D4ED8" };
  })();
  const deadlineDays = p.deadline ? Math.ceil((new Date(p.deadline + "T23:59:59") - Date.now()) / 86400000) : null;

  const patch = async (fields, msg) => {
    try {
      await api.patch(`/graphisme/projects/${p.id}`, fields);
      if (msg) toast.success(msg);
      load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveDesc = () => {
    if (desc !== (p.description || p.brief || "")) patch({ description: desc }, "Description enregistrée");
  };
  const menuAction = async (id) => {
    setMenuOpen(false);
    try {
      if (id === "open") navigate(`/graphisme/projet/${p.id}`);
      else if (id === "edit") onEdit?.(p);
      else if (id === "quote") {
        if (p.quote_id) navigate(`/comptabilite?tab=devis&quote=${p.quote_id}`);
        else { const r = await api.post(`/graphisme/projects/${p.id}/quote`, {}); toast.success("Devis créé (brouillon)"); navigate(`/comptabilite?tab=devis&quote=${r.data.id}`); }
      } else if (id === "duplicate") {
        await api.post(`/graphisme/projects/${p.id}/duplicate`);
        toast.success("Projet dupliqué"); onChanged?.();
      } else if (id === "archive") {
        await api.post(`/graphisme/projects/${p.id}/archive`, { archived: true });
        toast.success("Projet archivé"); onChanged?.();
      }
    } catch (e) { toast.error(apiError(e)); }
  };
  const uploadVersion = async (file) => {
    if (!file || !versionTarget) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", versionKind);
      await api.post(`/graphisme/projects/${p.id}/creations/${versionTarget}/versions`, fd);
      toast.success(versionKind === "travail" ? "Brouillon ajouté (invisible client)" : "Nouvelle version ajoutée");
      load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const sendValidation = async (cid) => {
    try {
      await api.post(`/graphisme/projects/${p.id}/creations/${cid}/send-validation`);
      toast.success("Envoyé au client pour validation");
      load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const postComment = async () => {
    if (!commentText.trim()) return;
    try {
      await api.post(`/graphisme/projects/${p.id}/comments`, { text: commentText.trim(), internal: commentInternal });
      setCommentText("");
      const { data } = await api.get(`/graphisme/projects/${p.id}/comments`);
      setComments(data || []);
      toast.success(commentInternal ? "Note interne ajoutée" : "Commentaire visible par le client publié");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden min-h-0" data-testid="gpanel">
      {/* En-tête projet */}
      <div className="px-4 pt-4 shrink-0">
        <div className="flex items-start gap-3">
          {thumb ? (
            <img src={fileUrl(thumb)} alt="" className="w-16 h-16 rounded-xl object-cover border border-neutral-100 shrink-0" data-testid="gpanel-thumb" />
          ) : (
            <span className="w-16 h-16 rounded-xl bg-[#F3F4F6] border border-neutral-100 flex items-center justify-center font-heading font-bold text-neutral-400 shrink-0" data-testid="gpanel-thumb">{initialsOf(p.name)}</span>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-base text-[#0F172A] truncate" data-testid="gpanel-name">{p.name}</h3>
                <Link to={`/clients/${p.client_id}`} className="text-xs font-medium text-[#0369A1] hover:underline" data-testid="gpanel-client">{p.client_name} ›</Link>
                {(p.types || []).length > 0 && <p className="text-[10px] text-neutral-400 mt-0.5">{p.types.join(" · ")}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <select value={p.status} onChange={(e) => patch({ status: e.target.value }, "Statut mis à jour")} data-testid="gpanel-status"
                  className={`appearance-none cursor-pointer text-[10px] font-semibold border rounded-full pl-2.5 pr-2 py-1 focus:outline-none ${st.cls}`}>
                  {(meta?.statuses || Object.keys(p ? {} : {})).map((s) => <option key={s} value={s}>{gstatus(s).label}</option>)}
                </select>
                <div className="relative">
                  <button onClick={() => setMenuOpen((v) => !v)} data-testid="gpanel-menu" className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 transition-colors">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                  {menuOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                      <div className="absolute right-0 top-8 z-40 w-52 bg-white border border-neutral-200 rounded-xl shadow-lg py-1" data-testid="gpanel-menu-list">
                        {[
                          ["open", FolderOpen, "Ouvrir le projet (page complète)"],
                          ["edit", FileText, "Modifier"],
                          ["quote", FileText, p.quote_id ? "Voir le devis" : "Créer un devis"],
                          ["duplicate", Copy, "Dupliquer"],
                          ["archive", Archive, "Archiver"],
                        ].map(([id, Icon, label]) => (
                          <button key={id} onClick={() => menuAction(id)} data-testid={`gpanel-action-${id}`}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50 transition-colors">
                            <Icon className="w-3.5 h-3.5 text-neutral-400" /> {label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 text-[11px] text-neutral-500 flex-wrap">
              {p.responsible && (
                <span className="flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-[#DBEAFE] text-[#1D4ED8] text-[8px] font-bold flex items-center justify-center">{initialsOf(p.responsible)}</span>
                  {p.responsible}
                </span>
              )}
              <span className="flex items-center gap-1" data-testid="gpanel-deadline">
                <CalendarDays className="w-3 h-3 text-neutral-400" />
                Livraison : <b className="text-neutral-700">{p.deadline ? fmtD(p.deadline) : "—"}</b>
                {deadlineDays != null && deadlineDays < 0 && <span className="text-[9px] font-semibold bg-red-50 text-red-600 border border-red-200 rounded-full px-1.5 py-px">En retard</span>}
                {deadlineDays != null && deadlineDays >= 0 && deadlineDays <= 3 && <span className="text-[9px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-px">J-{deadlineDays}</span>}
              </span>
              {p.price_ht > 0 && <span className="font-semibold text-neutral-700">{eur(p.price_ht)}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex items-center gap-5 px-4 mt-2 border-b border-neutral-100 shrink-0" data-testid="gpanel-tabs">
        {[["details", "Détails"], ["fichiers", "Fichiers"], ["commentaires", "Commentaires"], ["historique", "Historique"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`gpanel-tab-${id}`}
            className={`pb-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === id ? "border-[#0B132B] text-[#0B132B]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
            {label}
          </button>
        ))}
        {p.status === "projet_valide" && (
          <button onClick={() => patch({ status: "termine" }, "Projet finalisé")} data-testid="gpanel-finalize"
            className="ml-auto mb-1.5 inline-flex items-center gap-1 text-[10px] font-semibold bg-[#0B132B] text-white rounded-full px-3 py-1 hover:bg-[#1C2A4F] transition-colors">
            <CheckCircle2 className="w-3 h-3" /> Finaliser le projet
          </button>
        )}
      </div>

      {/* Contenu */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4">
        {tab === "details" && (
          <>
            <section>
              <h4 className="text-xs font-bold text-[#0F172A] mb-1">Description du projet</h4>
              <textarea value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={saveDesc} rows={3}
                data-testid="gpanel-description" placeholder="Brief, résumé du besoin client… (modifiable, enregistré au clic hors du champ)"
                className="w-full text-xs text-neutral-600 leading-relaxed bg-transparent rounded-lg px-2 py-1.5 -ml-2 hover:bg-neutral-50 focus:bg-white focus:border focus:border-neutral-200 focus:outline-none resize-y transition-colors" />
            </section>

            <div className="grid grid-cols-3 gap-2" data-testid="gpanel-infos">
              <div className="border border-neutral-100 rounded-xl p-2.5">
                <span className="w-6 h-6 rounded-lg bg-[#DBEAFE] flex items-center justify-center mb-1.5"><RefreshCcw className="w-3 h-3 text-[#1D4ED8]" /></span>
                <p className="text-[10px] text-neutral-400">Série de révisions</p>
                <p className="text-sm font-bold text-[#0F172A]">{modCount} / {p.corrections_included || 0}</p>
                <p className="text-[9px] text-neutral-400">tours effectués</p>
              </div>
              <div className="border border-neutral-100 rounded-xl p-2.5">
                <span className="w-6 h-6 rounded-lg flex items-center justify-center mb-1.5" style={{ background: validationState.bg }}><CheckCircle2 className="w-3 h-3" style={{ color: validationState.fg }} /></span>
                <p className="text-[10px] text-neutral-400">État de validation</p>
                <p className={`text-sm font-bold ${validationState.cls}`}>{validationState.label}</p>
                <p className="text-[9px] text-neutral-400">{validationState.sub}</p>
              </div>
              <div className="border border-neutral-100 rounded-xl p-2.5">
                <span className="w-6 h-6 rounded-lg bg-[#F1F5F9] flex items-center justify-center mb-1.5"><FileText className="w-3 h-3 text-[#64748B]" /></span>
                <p className="text-[10px] text-neutral-400">Format</p>
                <p className="text-sm font-bold text-[#0F172A] truncate">{p.formats || "—"}</p>
                <p className="text-[9px] text-neutral-400 truncate">{p.dimensions || ""}</p>
              </div>
            </div>

            <section>
              <div className="flex items-center gap-2 mb-2">
                <h4 className="text-xs font-bold text-[#0F172A] flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5 text-neutral-400" /> Fichiers joints ({allFiles.length})</h4>
                <button onClick={() => setTab("fichiers")} className="ml-auto text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid="gpanel-files-all">Tout télécharger ›</button>
              </div>
              {allFiles.length === 0 && <p className="text-[11px] text-neutral-400">Aucun fichier pour l'instant — ajoutez une version dans l'onglet Fichiers.</p>}
              <div className="grid grid-cols-3 gap-2">
                {allFiles.slice(0, 3).map((f) => {
                  const m = fileMeta(f.name, f.content_type);
                  const previewable = PREVIEW_IMG.includes(f.content_type) && f.id;
                  return (
                    <button key={f.id || f.name} onClick={() => f.id && window.open(fileUrl(f.id), "_blank")} data-testid={`gpanel-file-${f.id || f.name}`}
                      className="border border-neutral-100 rounded-xl overflow-hidden text-left hover:border-neutral-300 transition-colors">
                      {previewable ? (
                        <img src={fileUrl(f.id)} alt="" className="w-full h-16 object-cover" />
                      ) : (
                        <span className="w-full h-16 flex items-center justify-center" style={{ background: m.bg }}><m.Icon className="w-5 h-5" style={{ color: m.fg }} /></span>
                      )}
                      <span className="block px-2 py-1.5">
                        <span className="block text-[10px] font-semibold text-[#0F172A] truncate">{f.name}</span>
                        <span className="block text-[9px] text-neutral-400">{fmtSize(f.size)}{f.at ? ` · ${fmtD(f.at.slice(0, 10))}` : ""}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-2">
                <h4 className="text-xs font-bold text-[#0F172A] flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5 text-neutral-400" /> Historique des versions</h4>
                <button onClick={() => setTab("historique")} className="ml-auto text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid="gpanel-history-all">Voir tout l'historique ›</button>
              </div>
              {versions.length === 0 && <p className="text-[11px] text-neutral-400">Les versions du projet apparaîtront ici.</p>}
              <div className="space-y-2.5">
                {versions.slice(0, 4).map((v) => {
                  const vc = gstatus(v.creation.status);
                  const vf = (v.files || []).find((f) => PREVIEW_IMG.includes(f.content_type));
                  return (
                    <div key={v.id} className="flex items-start gap-2.5" data-testid={`gpanel-version-${v.id}`}>
                      <span className="w-2 h-2 rounded-full bg-[#3B82F6] mt-1.5 shrink-0" />
                      {vf ? <img src={fileUrl(vf.id)} alt="" className="w-9 h-9 rounded-lg object-cover border border-neutral-100 shrink-0" />
                        : <span className="w-9 h-9 rounded-lg bg-neutral-50 border border-neutral-100 flex items-center justify-center shrink-0"><FileIcon className="w-3.5 h-3.5 text-neutral-300" /></span>}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-[#0F172A]">{v.label} <span className="font-normal text-neutral-400">— {v.creation.name}</span></p>
                        <p className="text-[10px] text-neutral-400">{fmtDT(v.at)}{v.by ? ` · Par ${v.by}` : ""}</p>
                        {v.comment && <p className="text-[10px] text-neutral-500 mt-0.5 truncate">{v.comment}</p>}
                      </div>
                      <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${vc.cls}`}>{vc.label}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {tab === "fichiers" && (
          <>
            <input ref={fileRef} type="file" className="hidden" data-testid="gpanel-file-input" onChange={(e) => uploadVersion(e.target.files?.[0])} />
            {(p.creations || []).length === 0 && (
              <div className="text-center py-8">
                <p className="text-xs text-neutral-400 mb-3">Aucune création dans ce projet.</p>
                <button onClick={() => navigate(`/graphisme/projet/${p.id}`)} className="text-xs font-semibold text-[#0369A1] hover:underline" data-testid="gpanel-goto-creations">Ouvrir le projet pour créer une création ›</button>
              </div>
            )}
            {(p.creations || []).map((c) => {
              const cs = gstatus(c.status === "validee" ? "projet_valide" : c.status === "envoyee_validation" ? "envoye_validation" : c.status === "modifications_demandees" ? "modifications_demandees" : "en_cours");
              return (
                <div key={c.id} className="border border-neutral-100 rounded-xl p-3 space-y-2" data-testid={`gpanel-creation-${c.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs font-bold text-[#0F172A]">{c.name}</p>
                    <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 ${cs.cls}`}>{cs.label}</span>
                    <div className="ml-auto flex items-center gap-1.5">
                      <select value={versionKind} onChange={(e) => setVersionKind(e.target.value)} data-testid={`gpanel-kind-${c.id}`}
                        className="text-[10px] border border-neutral-200 rounded-lg px-1.5 py-1 bg-white focus:outline-none">
                        <option value="travail">Brouillon (interne)</option>
                        <option value="apercu">Aperçu client</option>
                        <option value="livraison">Fichier final</option>
                      </select>
                      <button onClick={() => { setVersionTarget(c.id); fileRef.current?.click(); }} disabled={busy} data-testid={`gpanel-add-version-${c.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold border border-neutral-200 rounded-lg px-2 py-1 hover:bg-neutral-50 disabled:opacity-50">
                        <Upload className="w-3 h-3" /> Version
                      </button>
                      {["prete", "en_cours"].includes(c.status) && (
                        <button onClick={() => sendValidation(c.id)} data-testid={`gpanel-send-${c.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold bg-[#0B132B] text-white rounded-lg px-2 py-1 hover:bg-[#1C2A4F]">
                          <Send className="w-3 h-3" /> Envoyer au client
                        </button>
                      )}
                    </div>
                  </div>
                  {(c.versions || []).filter((v) => !v.archived).slice().reverse().map((v) => (
                    <div key={v.id} className="pl-2 border-l-2 border-neutral-100 space-y-1">
                      <p className="text-[10px] font-semibold text-neutral-500">{v.label} · {fmtDT(v.at)}{v.by ? ` · ${v.by}` : ""}</p>
                      {(v.files || []).map((f) => {
                        const m = fileMeta(f.name, f.content_type);
                        return (
                          <div key={f.id} className="flex items-center gap-2 text-xs group" data-testid={`gpanel-vfile-${f.id}`}>
                            <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: m.bg }}><m.Icon className="w-3 h-3" style={{ color: m.fg }} /></span>
                            <span className="flex-1 min-w-0 truncate text-neutral-700">{f.name}</span>
                            <span className={`text-[9px] border rounded-full px-1.5 py-px ${KIND_CLS[f.kind] || KIND_CLS.travail}`}>{KIND_LABELS[f.kind] || f.kind}</span>
                            <span className="text-[10px] text-neutral-400 shrink-0">{fmtSize(f.size)}</span>
                            {f.id && (
                              <button onClick={() => window.open(fileUrl(f.id), "_blank")} data-testid={`gpanel-dl-${f.id}`}
                                className="p-1 rounded text-neutral-300 hover:text-neutral-700 hover:bg-neutral-100" title="Télécharger">
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {(c.mod_requests || []).length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                      <p className="text-[10px] font-bold text-amber-800">Modification demandée</p>
                      {c.mod_requests.slice(-1).map((mr, i) => (
                        <p key={i} className="text-[10px] text-amber-700 mt-0.5">{mr.text || mr.comment || ""} {mr.at ? `— ${fmtDT(mr.at)}` : ""}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        {tab === "commentaires" && (
          <>
            {comments.length === 0 && <p className="text-[11px] text-neutral-400" data-testid="gpanel-comments-empty">Aucun commentaire pour le moment.</p>}
            <div className="space-y-2.5">
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-2.5" data-testid={`gpanel-comment-${c.id}`}>
                  <span className="w-7 h-7 rounded-full bg-[#DBEAFE] text-[#1D4ED8] text-[9px] font-bold flex items-center justify-center shrink-0">{initialsOf(c.author_name)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-neutral-400">
                      <b className="text-neutral-700">{c.author_name}</b> · {relTime(c.created_at)} ·{" "}
                      <span className={`font-semibold ${c.internal ? "text-amber-700" : "text-blue-700"}`}>{c.internal ? "Interne DGA" : "Visible par le client"}</span>
                    </p>
                    <p className="text-xs text-neutral-600 whitespace-pre-line mt-0.5">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-neutral-200 rounded-xl p-2.5 space-y-2">
              <textarea rows={2} value={commentText} onChange={(e) => setCommentText(e.target.value)} data-testid="gpanel-comment-input"
                placeholder="Écrire un commentaire…" className="w-full text-xs focus:outline-none resize-y" />
              <div className="flex items-center gap-2">
                <button onClick={() => setCommentInternal(true)} data-testid="gpanel-comment-internal"
                  className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${commentInternal ? "bg-amber-100 border-amber-300 text-amber-800" : "border-neutral-200 text-neutral-400"}`}>
                  Interne DGA
                </button>
                <button onClick={() => setCommentInternal(false)} data-testid="gpanel-comment-client"
                  className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${!commentInternal ? "bg-blue-100 border-blue-300 text-blue-800" : "border-neutral-200 text-neutral-400"}`}>
                  Visible par le client
                </button>
                {!commentInternal && <span className="text-[9px] text-blue-600">Sera visible dans l'espace client + notification</span>}
                <button onClick={postComment} disabled={!commentText.trim()} data-testid="gpanel-comment-send"
                  className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold bg-[#0B132B] text-white rounded-lg px-3 py-1.5 hover:bg-[#1C2A4F] disabled:opacity-50">
                  <MessageSquare className="w-3 h-3" /> Publier
                </button>
              </div>
            </div>
          </>
        )}

        {tab === "historique" && (
          <>
            {(p.activity || []).length === 0 && <p className="text-[11px] text-neutral-400">Les actions du projet apparaîtront ici.</p>}
            <div className="space-y-2.5" data-testid="gpanel-activity">
              {[...(p.activity || [])].reverse().map((a, i) => (
                <div key={a.id || i} className="flex items-start gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-[#3B82F6] mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-neutral-700">{a.text || a.message || a.action || ""}</p>
                    <p className="text-[10px] text-neutral-400">{fmtDT(a.at)}{a.by ? ` · ${a.by}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
